import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, IconButton, Chip, TextField, Checkbox, FormControlLabel, Tabs, Tab } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface LogEntry {
  type: 'log' | 'error' | 'warn';
  message: string;
  timestamp: number;
}

export const DebugConsole: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState({ log: true, error: true, warn: true });
  const [search, setSearch] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;

    console.log = (...args: any[]) => {
      setLogs(prev => [...prev, { type: 'log', message: args.map(a => JSON.stringify(a)).join(' '), timestamp: Date.now() }]);
      originalLog(...args);
    };

    console.error = (...args: any[]) => {
      setLogs(prev => [...prev, { type: 'error', message: args.map(a => JSON.stringify(a)).join(' '), timestamp: Date.now() }]);
      originalError(...args);
    };

    console.warn = (...args: any[]) => {
      setLogs(prev => [...prev, { type: 'warn', message: args.map(a => JSON.stringify(a)).join(' '), timestamp: Date.now() }]);
      originalWarn(...args);
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const copyLogs = () => {
    const text = filteredLogs.map(log => `[${new Date(log.timestamp).toLocaleTimeString()}] ${log.type.toUpperCase()}: ${log.message}`).join('\n');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  const clearLogs = () => setLogs([]);

  const filteredLogs = logs.filter(log => {
    if (!filter[log.type]) return false;
    if (search && !log.message.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const errorCount = logs.filter(l => l.type === 'error').length;
  const warnCount = logs.filter(l => l.type === 'warn').length;

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        maxHeight: collapsed ? '40px' : '300px',
        bgcolor: '#1e1e1e',
        color: '#d4d4d4',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5, borderBottom: collapsed ? 'none' : '1px solid #333' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#569cd6', fontWeight: 'bold' }}>
            Debug Console
          </Typography>
          {errorCount > 0 && <Chip label={errorCount} size="small" sx={{ bgcolor: '#f44336', color: '#fff', height: '18px', fontSize: '10px' }} />}
          {warnCount > 0 && <Chip label={warnCount} size="small" sx={{ bgcolor: '#ff9800', color: '#fff', height: '18px', fontSize: '10px' }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={clearLogs} sx={{ color: '#d4d4d4', p: 0.5 }}>
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: '8px' }} />
          <IconButton size="small" onClick={copyLogs} sx={{ color: '#d4d4d4', p: 0.5 }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: '8px' }} />
          <IconButton size="small" onClick={() => setCollapsed(!collapsed)} sx={{ color: '#d4d4d4', p: 0.5 }}>
            {collapsed ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      {!collapsed && (
        <>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #333', minHeight: '32px', '& .MuiTab-root': { minHeight: '32px', fontSize: '11px', color: '#888', '&.Mui-selected': { color: '#569cd6' } } }}>
            <Tab label="Console" />
            <Tab label="Storage" />
          </Tabs>
          {activeTab === 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, p: 0.5, borderBottom: '1px solid #333', flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={filter.log} onChange={(e) => setFilter({ ...filter, log: e.target.checked })} sx={{ color: '#4ec9b0', p: 0.5 }} />}
                  label={<Typography variant="caption">LOG</Typography>}
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={filter.error} onChange={(e) => setFilter({ ...filter, error: e.target.checked })} sx={{ color: '#f44336', p: 0.5 }} />}
                  label={<Typography variant="caption">ERROR</Typography>}
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={filter.warn} onChange={(e) => setFilter({ ...filter, warn: e.target.checked })} sx={{ color: '#ff9800', p: 0.5 }} />}
                  label={<Typography variant="caption">WARN</Typography>}
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} sx={{ color: '#569cd6', p: 0.5 }} />}
                  label={<Typography variant="caption">Auto-scroll</Typography>}
                  sx={{ m: 0 }}
                />
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flex: 1, minWidth: '100px', '& .MuiInputBase-root': { fontSize: '11px', color: '#d4d4d4', bgcolor: '#2d2d2d' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' } }}
                />
              </Box>
              <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', p: 1, fontSize: '11px', fontFamily: 'monospace' }}>
                {filteredLogs.map((log, i) => {
                  const color = log.type === 'error' ? '#f44336' : log.type === 'warn' ? '#ff9800' : '#4ec9b0';
                  const time = new Date(log.timestamp).toLocaleTimeString();
                  return (
                    <Box key={i} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', mb: 0.5 }}>
                      <span style={{ color: '#888' }}>[{time}]</span> <span style={{ color, fontWeight: 'bold' }}>{log.type.toUpperCase()}:</span> {log.message}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
          {activeTab === 1 && (
            <Box sx={{ flex: 1, overflow: 'auto', p: 1, fontSize: '11px', fontFamily: 'monospace' }}>
              <Typography variant="caption" sx={{ color: '#569cd6', fontWeight: 'bold', mb: 1, display: 'block' }}>LocalStorage:</Typography>
              {Object.keys(localStorage).map(key => (
                <Box key={key} sx={{ mb: 1 }}>
                  <span style={{ color: '#4ec9b0' }}>{key}:</span> <span style={{ color: '#ce9178' }}>{localStorage.getItem(key)?.substring(0, 100)}{(localStorage.getItem(key)?.length || 0) > 100 ? '...' : ''}</span>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};
